import { useTranslation } from 'react-i18next'
import { Link, useLocation, useParams } from 'react-router'
import { useQuery } from '@apollo/client/react'

import Document from 'js/components/Document'
import Flexer from 'js/components/Flexer/Flexer'
import { Container } from 'js/components/Grid'
import { H1 } from 'js/components/Heading'
import TabMenu from 'js/components/Navigation/TabMenu'
import TabPanel from 'js/components/Navigation/TabPanel'
import Section from 'js/components/Section/Section'
import Text from 'js/components/Text/Text'
import { NotFound } from 'js/core/NotFound'
import EventDataSummary from 'js/events/components/EventDataSummary'
import { onlyTypename } from 'js/lib/helpers/onlyTypename'

import { GET_EVENT_GUEST_LIST_WRAPPER } from './GuestList.queries'
import List from './List'

export const EventGuestListPage = () => {
    const params = useParams()
    const hash = useLocation().hash?.slice(1) || ''

    const { guid } = params

    const { loading, data } = useQuery(GET_EVENT_GUEST_LIST_WRAPPER, {
        variables: {
            guid,
        },
    })

    const { t } = useTranslation()

    if (loading) {
        return null
    }

    const event = onlyTypename(data?.entity, 'Event')

    if (!event || !event.canEdit) {
        return <NotFound />
    }

    const { viewer } = data
    const { title, url, attendees } = event

    const {
        totalCheckedIn,
        totalAcceptNotCheckedIn,
        totalWaitinglistNotCheckedIn,
    } = attendees

    return (
        <>
            <Document
                title={t('entity-event.guest-list')}
                containerTitle={title}
            />

            <Section backgroundColor="white" grow>
                <Container>
                    <Flexer justifyContent="flex-start" wrap>
                        <div style={{ flexGrow: 1 }}>
                            <H1>{t('entity-event.guest-list')}</H1>
                            <Text>
                                <Link to={url}>{title}</Link>
                            </Text>
                        </div>
                        <EventDataSummary
                            entity={event}
                            viewer={viewer}
                            style={{ padding: '4px 0' }}
                        />
                    </Flexer>
                    <TabMenu
                        label={t('entity-event.guest-list')}
                        showBorder
                        options={[
                            {
                                label: `${t(
                                    'entity-event.checked-in',
                                )} (${totalCheckedIn})`,
                                link: '',
                                isActive: !hash,
                                id: 'tab-checked-in',
                                ariaControls: 'tabpanel-checked-in',
                            },
                            {
                                label: `${t(
                                    'entity-event.going',
                                )} (${totalAcceptNotCheckedIn})`,
                                link: '#going',
                                isActive: hash === 'going',
                                id: 'tab-going',
                                ariaControls: 'tabpanel-going',
                            },
                            {
                                label: `${t(
                                    'entity-event.queue',
                                )} (${totalWaitinglistNotCheckedIn})`,
                                link: '#waiting',
                                isActive: hash === 'waiting',
                                id: 'tab-waiting',
                                ariaControls: 'tabpanel-waiting',
                            },
                        ]}
                        sticky
                    />
                    <TabPanel
                        visible={!hash || hash === 'checked-in'}
                        id={`tabpanel-checked-in`}
                        aria-labelledby="tab-checked-in"
                    >
                        <List guid={guid} state={null} />
                    </TabPanel>
                    <TabPanel
                        visible={hash === 'going'}
                        id={`tabpanel-going`}
                        aria-labelledby="tab-going"
                    >
                        <List guid={guid} state="accept" />
                    </TabPanel>
                    <TabPanel
                        visible={hash === 'waiting'}
                        id={`tabpanel-waiting`}
                        aria-labelledby="tab-waiting"
                    >
                        <List guid={guid} state="waitinglist" />
                    </TabPanel>
                </Container>
            </Section>
        </>
    )
}
